import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile } from '../lib/auth/profileManager';

// Ensure we're using the correct User type from Firebase Auth
type FirebaseUser = User;

interface UserProfileData {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  username?: string;
  subscriptionStatus?: string;
  createdAt?: string;
  [key: string]: any; // To accommodate any additional fields
}

export const useUserProfile = () => {
  const { user: authUser, loading: authLoading, logOut: authLogOut } = useAuth();
  const user = authUser as FirebaseUser | null;
  const logOut = authLogOut as (() => Promise<void>) | undefined;
  const [userData, setUserData] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch user data when user is available
  useEffect(() => {
    const fetchUserData = async () => {
      if (user && typeof getUserProfile === 'function') {
        setIsLoading(true);
        setError(null);
        try {
          const data = await getUserProfile(user.uid);
          setUserData(data ? { ...data, id: user.uid } : null);
        } catch (err) {
          console.error('Error fetching user data in useUserProfile:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch user data'));
          setUserData(null);
        } finally {
          setIsLoading(false);
        }
      } else if (!user) {
        setUserData(null);
        setError(null);
      }
    };

    fetchUserData();
  }, [user]);

  // Get user's display name
  const getUserDisplayName = (): string => {
    if (userData && (userData.firstName || userData.lastName)) {
      return `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
    }
    if (userData && userData.displayName) {
      return userData.displayName;
    }
    if (user && user.email) {
      // If we don't have a name, use the email but truncate the domain part
      return user.email.split('@')[0];
    }
    return 'User';
  };

  // User profile photo URL
  const getUserPhotoURL = (): string => {
    if (userData && userData.photoURL) {
      return userData.photoURL;
    }
    if (user && user.photoURL) {
      return user.photoURL;
    }
    return '/assets/images/pages/profile.webp'; // Default avatar
  };

  // Handle user logout
  const handleLogout = async (): Promise<void> => {
    if (!logOut) {
      console.error("Logout function not available from AuthContext");
      setError(new Error('Logout unavailable'));
      return;
    }
    try {
      setError(null);
      await logOut();
    } catch (error) {
      console.error('Error logging out:', error);
      setError(error instanceof Error ? error : new Error('Failed to log out'));
    }
  };

  // Determine the final loading state more robustly
  const combinedLoading = authLoading || (authUser && isLoading);

  return {
    user,
    userData,
    // Return the more robust combined loading state
    loading: combinedLoading,
    error,
    getUserDisplayName,
    getUserPhotoURL,
    handleLogout
  };
};
